// GR02 조회조건 저장/복원 — 순수 변환 (엔트리 index.tsx 의 배선이 소비, 테스트 대상)
//
// 마지막 조회 조건을 메뉴 세션(sessionStorage)·URL 쿼리에 남겨 다른 메뉴를 다녀와도 유지한다(MOMS 공통 UX).
// 저장 경로는 `MenuStorage.menuOnSession.setSearch` → `Utils.qsStringify`(arrayFormat: comma),
// 복원 경로는 `useParsedQuery`(qs.parse comma) 다. 그래서 왕복 형태를 그 두 함수에 맞춘다(GR05 준용).
//
// D16(TPM 정정) — `@/hooks/useSessionPersistedState` 는 쓰지 않는다. PR #688 에서 걷어냈고
// 이 레포의 13개 화면이 이미 이 방식(화면별 searchQuery.ts + MenuStorage + URL 복원)을 쓰고 있다.
//
// 저장 대상은 값을 되돌려 심을 통로가 있는 필드로 한정한다(D16). 이 화면은 D2 로 `FilterArea` 를
// 쓰므로 `onFormReady(form)` 로 받은 form 인스턴스에 `setFieldsValue` 로 전 필드를 심을 수 있다 —
// erpInbound(`FilterCollapse` 조합이라 통로가 없던 화면)와 달리 필터 필드를 전부 저장·복원한다.
// 페이지 번호와 목록 검색어도 함께 싣는다 — 조건만 남고 페이지·검색어가 초기화되면 사용자에게는
// 여전히 새로고침으로 보인다.
import dayjs from 'dayjs'
import { DATE_FORMAT, FIELD_NAMES } from './constants'
import type { InboundInquiryFormValues } from './filterToRequest'

/** URL·세션에 남는 형태. 값이 있는 키만 싣는다(qsStringify 가 nil 은 걸러 주지만 빈 배열·빈 문자열은 남긴다). */
export interface InboundInquirySearchQuery {
  serviceTypes?: string[]
  /** `logistics.ShipperSelect` 가 배열로 주는 값을 그대로 싣는다(filterToRequest.ts 주석 참조 — BE 는 첫 값만 쓴다). */
  shipperCode?: string[]
  ordOptionNumbers?: string[]
  /** 대표입고번호 — BE `orderNumbers`(ORD_NO) IN. */
  orderNumbers?: string[]
  orderType?: string
  warehouseName?: string
  plnDateFrom?: string
  plnDateTo?: string
  actDate?: string
  /** HEAD 표 위 목록 검색어. 받은 행을 거르는 클라이언트 필터다. */
  keyword?: string
}

/**
 * 폼 값이 배열/단건 문자열 어느 쪽으로 와도 문자열 목록으로 맞춘다(mfs.FormTextArea 는 양쪽 다 준다).
 * `filterToRequest.ts` 의 `toTrimmedList`(항상 `string[]`)와 로직은 같지만 여기서는 빈 목록을 `undefined` 로 돌린다 —
 * `qsStringify` 가 `undefined` 키를 URL 에서 빼기 때문에, 저장용은 빈 배열 대신 키가 없어야 한다(의도된 차이).
 */
const toList = (v?: string[] | string): string[] | undefined => {
  let list: string[]
  if (Array.isArray(v)) {
    list = v
  } else if (typeof v === 'string' && v.trim()) {
    list = [v.trim()]
  } else {
    list = []
  }
  const cleaned = list.map((x) => String(x).trim()).filter(Boolean)
  return cleaned.length ? cleaned : undefined
}

/** 날짜 필드에서 `YYYY-MM-DD` 문자열을 꺼낸다. 기간으로 와도 단일 값으로 와도 받는다. */
const toDateString = (v: unknown): string | undefined => {
  if (!v) return undefined
  const d = dayjs(v as dayjs.ConfigType)
  return d.isValid() ? d.format(DATE_FORMAT) : undefined
}

/** 저장 변환 옵션 — `filterToRequest.ts` `ToInboundsReqOptions` 와 같은 모양으로 맞춘다. */
export interface ToSearchQueryOptions {
  /**
   * 대표입고번호 필터 노출 여부(`INBOUND_INQUIRY_ORDER_NO_FILTER_FLAG`). 기본값은 꺼짐이다 —
   * 옵션을 빠뜨린 호출부가 플래그를 우회해 `orderNumbers` 를 남기지 않게 fail-closed 로 둔다.
   */
  isOrderNoFilterEnabled?: boolean
}

/**
 * 폼 값 + 검색어 상태 → 저장 형태. `undefined` 키는 qsStringify 가 걸러 낸다.
 * 페이지는 싣지 않는다 — 전량을 받아 화면이 나누므로 페이지 이동이 조회 조건이 아니다(LOGISPRD-15268).
 *
 * 대표입고번호는 플래그가 켜졌을 때만 남긴다. 조회 요청만 막으면 부족하다 — pbo-core `FilterArea` 는
 * `onSearch?.(form.getFieldsValue(true), form)` 로 **렌더되지 않은 필드까지 포함해** 폼 store 전체를
 * 넘기므로(0.7.8 `src/components/Filter.tsx:1095`), 칸이 안 그려져도 복원값이 그대로 돌아 다시
 * URL·세션에 실린다. 그러면 플래그를 껐다 켜는 순간 사용자가 넣은 적 없는 조건으로 복원 조회가 걸린다.
 */
export const toSearchQuery = (
  values: InboundInquiryFormValues,
  keyword: string,
  { isOrderNoFilterEnabled = false }: ToSearchQueryOptions = {},
): InboundInquirySearchQuery => {
  const pln = values[FIELD_NAMES.plnDateRange]
  const [plnFrom, plnTo] = Array.isArray(pln) ? [pln[0], pln[1]] : [pln, pln]
  const act = values[FIELD_NAMES.actDate]

  return {
    serviceTypes: toList(values[FIELD_NAMES.serviceTypes]),
    shipperCode: toList(values[FIELD_NAMES.shipperCode]),
    ordOptionNumbers: toList(values[FIELD_NAMES.ordOptionNumbers]),
    orderNumbers: isOrderNoFilterEnabled ? toList(values[FIELD_NAMES.orderNumbers]) : undefined,
    plnDateFrom: toDateString(plnFrom),
    plnDateTo: toDateString(plnTo),
    actDate: toDateString(Array.isArray(act) ? act[0] : act),
    orderType: values[FIELD_NAMES.orderType] || undefined,
    warehouseName: values[FIELD_NAMES.warehouseName] || undefined,
    // Utils.qsStringify 는 encode:false 라 & # , 를 그대로 URL 에 싣는다 — 자유 입력 문자열은 직접 인코딩해야
    // 뒤 파라미터를 삼키거나(&) 프래그먼트로 잘리거나(#) comma 파서에 배열로 갈리지 않는다(comma 파서는
    // qs.parse 기본 decoder 가 %xx 를 자동으로 되돌리므로 fromSearchQuery 쪽은 손댈 필요가 없다).
    keyword: keyword ? encodeURIComponent(keyword) : undefined,
  }
}

/**
 * 문자열 목록만 남긴다 — comma 파서는 값이 하나면 배열이 아니라 문자열을 준다(`a,b` → `['a','b']`, `a` → `'a'`).
 * 조작된 URL(객체·중첩 배열)은 버린다.
 */
export const asRestoredList = (v: unknown): string[] | undefined => {
  if (Array.isArray(v)) {
    const cleaned = v.filter((x) => typeof x === 'string' || typeof x === 'number').map(String)
    return cleaned.length ? cleaned : undefined
  }
  return typeof v === 'string' && v.trim() ? [v.trim()] : undefined
}

/** `YYYY-MM-DD` 문자열 → Dayjs. 형식이 어긋나면 버린다(조작된 URL 이 날짜 칸을 Invalid Date 로 만들지 않게). */
const asRestoredDate = (v: unknown) => {
  if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return null
  const parsed = dayjs(v)
  return parsed.isValid() ? parsed : null
}

export interface RestoredInboundInquirySearch {
  values: InboundInquiryFormValues
  keyword: string
}

/**
 * URL 쿼리(파싱 결과) → 복원 조회조건.
 *
 * 화주 필터(화주유형 또는 화주명)가 없으면 복원 대상이 아니다(undefined) — 그 상태로 조회하면
 * 화면이 막는 필수 필터(hasRequiredFilter)에 걸리므로 기본값으로 여는 편이 맞다(GR05 준용).
 */
export const fromSearchQuery = (query: Record<string, unknown>): RestoredInboundInquirySearch | undefined => {
  const serviceTypes = asRestoredList(query.serviceTypes)
  const shipperCode = asRestoredList(query.shipperCode)
  if (!serviceTypes && !shipperCode) return undefined

  const plnFrom = asRestoredDate(query.plnDateFrom)
  const plnTo = asRestoredDate(query.plnDateTo)

  return {
    values: {
      [FIELD_NAMES.serviceTypes]: serviceTypes ?? [],
      [FIELD_NAMES.shipperCode]: shipperCode ?? [],
      [FIELD_NAMES.ordOptionNumbers]: asRestoredList(query.ordOptionNumbers) ?? [],
      [FIELD_NAMES.orderNumbers]: asRestoredList(query.orderNumbers) ?? [],
      [FIELD_NAMES.plnDateRange]: plnFrom || plnTo ? [plnFrom, plnTo] : null,
      [FIELD_NAMES.actDate]: asRestoredDate(query.actDate),
      [FIELD_NAMES.orderType]: typeof query.orderType === 'string' ? query.orderType : '',
      [FIELD_NAMES.warehouseName]: typeof query.warehouseName === 'string' ? query.warehouseName : '',
    },
    keyword: typeof query.keyword === 'string' ? query.keyword : '',
  }
}
